import React, { FC } from 'react';
import styled from 'styled-components';
import {
  OvulatoryEventExcludedReason,
  OvulatoryEventScorecard,
} from '../../../common/model/type/ovulatory-event-prediction.type';
import { formatOvulatoryEventRate } from '../../../common/utils/services/ovulatory-event-prediction-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import {
  OVULATORY_EVENT_EXCLUDED_LABELS,
  OVULATORY_EVENT_OUTCOME_DESCRIPTIONS,
  OVULATORY_EVENT_OUTCOME_LABELS,
  OVULATORY_EVENT_STATE_SHORT_LABELS,
  OVULATORY_EVENT_WRONG_LABELS,
} from '../organisms/ovulatory-event-prediction-presentation';

interface OvOvulatoryEventScorecardProps {
  scorecard: OvulatoryEventScorecard;
  scope: string;
}

const EXCLUDED_ORDER: OvulatoryEventExcludedReason[] = [
  'unknown',
  'pending',
  'event_on_day',
  'ineligible',
];

const NO_EVALUABLE_ROWS = 'No evaluable rows';

const OvOvulatoryEventScorecard: FC<OvOvulatoryEventScorecardProps> = ({
  scorecard: {
    approachingPositive,
    approachingNegative,
    notApproachingPositive,
    notApproachingNegative,
    counted,
    precision,
    recall,
    excluded,
  },
  scope,
}) => (
  <Layout aria-labelledby="ovulatory-event-scorecard-title">
    <Heading>
      <Title id="ovulatory-event-scorecard-title">Scorecard</Title>
      <Meta>{scope}</Meta>
      <Meta>
        {counted} evaluated {counted === 1 ? 'row' : 'rows'}
      </Meta>
    </Heading>

    <Matrix>
      <thead>
        <tr>
          <td />
          <th scope="col" title={OVULATORY_EVENT_OUTCOME_DESCRIPTIONS.positive}>
            {OVULATORY_EVENT_OUTCOME_LABELS.positive}
          </th>
          <th
            scope="col"
            title={OVULATORY_EVENT_OUTCOME_DESCRIPTIONS.sufficient_negative}
          >
            {OVULATORY_EVENT_OUTCOME_LABELS.sufficient_negative}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">{OVULATORY_EVENT_STATE_SHORT_LABELS.approaching}</th>
          <Cell data-testid="scorecard-approaching-positive">
            <strong>{approachingPositive}</strong> Hit
          </Cell>
          <Cell
            $wrong={approachingNegative > 0}
            data-testid="scorecard-approaching-negative"
          >
            <strong>{approachingNegative}</strong>{' '}
            {OVULATORY_EVENT_WRONG_LABELS.false_alarm}
          </Cell>
        </tr>
        <tr>
          <th scope="row">
            {OVULATORY_EVENT_STATE_SHORT_LABELS.not_approaching}
          </th>
          <Cell
            $wrong={notApproachingPositive > 0}
            data-testid="scorecard-not-approaching-positive"
          >
            <strong>{notApproachingPositive}</strong>{' '}
            {OVULATORY_EVENT_WRONG_LABELS.miss}
          </Cell>
          <Cell data-testid="scorecard-not-approaching-negative">
            <strong>{notApproachingNegative}</strong> Correct negative
          </Cell>
        </tr>
      </tbody>
    </Matrix>

    <Metric data-testid="scorecard-precision">
      <Kicker>Precision</Kicker>
      <MetricValue>
        {precision === null ? '—' : formatOvulatoryEventRate(precision)}
      </MetricValue>
      <Meta>
        {precision === null
          ? NO_EVALUABLE_ROWS
          : `${approachingPositive} / ${
              approachingPositive + approachingNegative
            }`}
      </Meta>
    </Metric>

    <Metric data-testid="scorecard-recall">
      <Kicker>Recall</Kicker>
      <MetricValue>
        {recall === null ? '—' : formatOvulatoryEventRate(recall)}
      </MetricValue>
      <Meta>
        {recall === null
          ? NO_EVALUABLE_ROWS
          : `${approachingPositive} / ${
              approachingPositive + notApproachingPositive
            }`}
      </Meta>
    </Metric>

    <Excluded>
      <Kicker>Excluded</Kicker>
      <ExcludedList>
        {EXCLUDED_ORDER.map((reason) => (
          <li key={reason} data-testid={`scorecard-excluded-${reason}`}>
            {excluded[reason]} {OVULATORY_EVENT_EXCLUDED_LABELS[reason]}
          </li>
        ))}
      </ExcludedList>
    </Excluded>
  </Layout>
);

export default OvOvulatoryEventScorecard;

const Layout = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem 2.5rem;
`;

const Heading = styled.div`
  min-width: 9rem;
`;

const Title = styled.h3`
  margin: 0 0 0.375rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.LARGE};
  font-weight: bold;
`;

const Meta = styled.p`
  margin: 0.375rem 0 0;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  font-variant-numeric: tabular-nums;
`;

const Kicker = styled.span`
  display: block;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Matrix = styled.table`
  border-collapse: separate;
  border-spacing: 0.375rem 0.25rem;
  margin: -0.25rem -0.375rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};

  th {
    padding: 0 0.625rem;
    color: ${Colours.OV_CHARCOAL};
    font-weight: normal;
    text-align: left;
    white-space: nowrap;
  }

  th[scope='row'] {
    padding-left: 0;
    padding-right: 1.5rem;
    color: ${Colours.OV_BASE};
  }
`;

const Cell = styled.td<{ $wrong?: boolean }>`
  min-width: 8.5rem;
  padding: 0.25rem 0.625rem;
  border-radius: ${Variables.borderRadius.SMALL};
  background: ${({ $wrong }) =>
    $wrong ? Colours.STRAWBERRY['50'] : 'transparent'};
  color: ${({ $wrong }) =>
    $wrong ? Colours.OV_ACCENT_MAGENTA : Colours.OV_CHARCOAL};
  white-space: nowrap;

  strong {
    display: inline-block;
    min-width: 1.5rem;
    margin-right: 0.25rem;
    color: ${({ $wrong }) =>
      $wrong ? Colours.OV_ACCENT_MAGENTA : Colours.OV_BASE};
    font-size: ${Variables.fontSizes.LARGE};
    font-variant-numeric: tabular-nums;
  }
`;

const Metric = styled.div`
  min-width: 6rem;
`;

const MetricValue = styled.span`
  display: block;
  margin-top: 0.5rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.XLARGE};
  font-weight: bold;
  font-variant-numeric: tabular-nums;
`;

const Excluded = styled.div`
  min-width: 13rem;
`;

const ExcludedList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 0.5rem 2.5rem;
  margin: 0.625rem 0 0;
  padding: 0;
  list-style: none;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  font-variant-numeric: tabular-nums;
`;
